import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Video, FolderPlus, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";

const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-blue-500/50 focus:outline-none transition-colors";

export default function AdminLearning() {
  const [courses, setCourses] = useState([]);
  const [newCourse, setNewCourse] = useState({ title: "", description: "" });
  const [videoForms, setVideoForms] = useState({});
  const [editing, setEditing] = useState(null); // video id being edited

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/learning");
      setCourses(data.courses || []);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCourse = async (e) => {
    e.preventDefault();
    if (!newCourse.title.trim()) return;
    try {
      await api.post("/admin/learning/courses", newCourse);
      setNewCourse({ title: "", description: "" });
      toast.success("Corso creato");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm("Eliminare il corso e tutti i suoi video?")) return;
    try {
      await api.delete(`/admin/learning/courses/${id}`);
      toast.success("Corso eliminato");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const vf = (cid) => videoForms[cid] || { title: "", url: "", description: "", duration: "" };
  const setVf = (cid, patch) => setVideoForms((p) => ({ ...p, [cid]: { ...vf(cid), ...patch } }));

  const addVideo = async (cid) => {
    const f = vf(cid);
    if (!f.title.trim() || !f.url.trim()) { toast.error("Titolo e URL sono obbligatori"); return; }
    try {
      await api.post("/admin/learning/videos", { course_id: cid, ...f });
      setVideoForms((p) => ({ ...p, [cid]: { title: "", url: "", description: "", duration: "" } }));
      toast.success("Video aggiunto");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveVideo = async (v) => {
    try {
      await api.put(`/admin/learning/videos/${v.id}`, {
        course_id: v.course_id, title: v.title, description: v.description,
        url: v.url, duration: v.duration,
      });
      toast.success("Video aggiornato");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Eliminare questo video?")) return;
    try {
      await api.delete(`/admin/learning/videos/${id}`);
      toast.success("Video eliminato");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const updateEditingField = (cid, vid, field, value) => {
    setCourses((prev) => prev.map((c) => c.id !== cid ? c : {
      ...c, videos: c.videos.map((v) => v.id === vid ? { ...v, [field]: value } : v),
    }));
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="admin-learning-page">
      <Link to="/admin" data-testid="admin-learning-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6">
        <ArrowLeft size={16} /> Torna al pannello Admin
      </Link>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Gestione Learning</h1>
      <p className="text-sm text-zinc-400 mb-8">Crea corsi e aggiungi video (link YouTube o Vimeo). Visibili solo agli abbonati VIP.</p>

      <form onSubmit={createCourse} className="bg-[#141414] border border-blue-500/30 rounded-md p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><FolderPlus size={18} className="text-blue-400" /> Nuovo corso</h2>
        <div className="space-y-3">
          <input data-testid="course-title-input" value={newCourse.title}
            onChange={(e) => setNewCourse((c) => ({ ...c, title: e.target.value }))}
            placeholder="Titolo del corso (es. Analisi Tecnica)" className={inputCls} />
          <input data-testid="course-desc-input" value={newCourse.description}
            onChange={(e) => setNewCourse((c) => ({ ...c, description: e.target.value }))}
            placeholder="Descrizione (opzionale)" className={inputCls} />
          <button data-testid="course-create-btn" type="submit"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors">
            <Plus size={16} /> Crea corso
          </button>
        </div>
      </form>

      <div className="space-y-6">
        {courses.length === 0 && <p className="text-sm text-zinc-500">Nessun corso ancora. Creane uno qui sopra.</p>}
        {courses.map((c) => (
          <div key={c.id} className="bg-[#141414] border border-white/10 rounded-md p-6" data-testid={`admin-course-${c.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{c.title}</h3>
                {c.description && <p className="text-sm text-zinc-500">{c.description}</p>}
                <p className="text-xs text-zinc-600 mt-1">{c.videos.length} video</p>
              </div>
              <button data-testid={`delete-course-${c.id}`} onClick={() => deleteCourse(c.id)}
                className="p-2 rounded-md text-zinc-500 hover:bg-red-500/15 hover:text-red-400 transition-colors" title="Elimina corso">
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-2 mb-5">
              {c.videos.map((v, i) => (
                <div key={v.id} className="bg-black/30 border border-white/5 rounded-md p-3" data-testid={`admin-video-${v.id}`}>
                  {editing === v.id ? (
                    <div className="space-y-2">
                      <input value={v.title} onChange={(e) => updateEditingField(c.id, v.id, "title", e.target.value)} className={inputCls} placeholder="Titolo" />
                      <input value={v.url} onChange={(e) => updateEditingField(c.id, v.id, "url", e.target.value)} className={inputCls} placeholder="URL YouTube/Vimeo" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={v.description || ""} onChange={(e) => updateEditingField(c.id, v.id, "description", e.target.value)} className={inputCls} placeholder="Descrizione" />
                        <input value={v.duration || ""} onChange={(e) => updateEditingField(c.id, v.id, "duration", e.target.value)} className={inputCls} placeholder="Durata (es. 12:30)" />
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`save-video-${v.id}`} onClick={() => saveVideo(v)} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500">Salva</button>
                        <button onClick={() => { setEditing(null); load(); }} className="px-3 py-1.5 rounded-md bg-white/5 text-zinc-400 text-xs hover:bg-white/10 flex items-center gap-1"><X size={12} /> Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {v.thumbnail
                        ? <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded shrink-0" />
                        : <div className="w-24 h-14 rounded bg-white/5 flex items-center justify-center shrink-0"><Video size={18} className="text-zinc-600" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{i + 1}. {v.title}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{v.provider} · {v.duration || "—"}</div>
                      </div>
                      <button data-testid={`edit-video-${v.id}`} onClick={() => setEditing(v.id)} className="p-1.5 rounded text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10" title="Modifica"><Pencil size={14} /></button>
                      <button data-testid={`delete-video-${v.id}`} onClick={() => deleteVideo(v.id)} className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10" title="Elimina"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-white/5 pt-4">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Plus size={13} /> Aggiungi video</p>
              <div className="space-y-2">
                <input data-testid={`video-title-${c.id}`} value={vf(c.id).title} onChange={(e) => setVf(c.id, { title: e.target.value })} placeholder="Titolo video" className={inputCls} />
                <input data-testid={`video-url-${c.id}`} value={vf(c.id).url} onChange={(e) => setVf(c.id, { url: e.target.value })} placeholder="URL YouTube o Vimeo" className={inputCls} />
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid={`video-desc-${c.id}`} value={vf(c.id).description} onChange={(e) => setVf(c.id, { description: e.target.value })} placeholder="Descrizione (opzionale)" className={inputCls} />
                  <input data-testid={`video-duration-${c.id}`} value={vf(c.id).duration} onChange={(e) => setVf(c.id, { duration: e.target.value })} placeholder="Durata (es. 12:30)" className={inputCls} />
                </div>
                <button data-testid={`add-video-btn-${c.id}`} onClick={() => addVideo(c.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors">
                  <Video size={15} /> Aggiungi video
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
